import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useFinancialPlan } from '../../context/FinancialPlanContext';
import { MAX_LIST_ROWS } from '../../utils/calculations/retirementPlan';
import { formatMoney } from '../../hooks/useResultsAnalysis';
import { track } from '../../services/analytics';
import { rentalIncomeRow } from '../../utils/rentalIncome';

const OPTIONS = [
  { key: 'ltr', name: 'long-term rental', label: 'Long-term rental', cash: (a) => a.annualCashFlow },
  { key: 'str', name: 'short-term rental', label: 'Short-term rental', cash: (a) => a.annualStrCashFlow },
];

/** Carries a rental's yearly cash flow into the retirement plan as other income. */
export default function AddToPlan({ analysis, eligible }) {
  const { state, dispatch } = useFinancialPlan();
  const [added, setAdded] = useState(null);
  const rows = Array.isArray(state.profile.otherIncome) ? state.profile.otherIncome : [];
  const options = OPTIONS.filter((option) => eligible[option.key]).map((option) => ({ ...option, amount: option.cash(analysis) }));
  if (options.length === 0) return null;
  const full = rows.length >= MAX_LIST_ROWS;

  const inPlan = (option) => rows.find((row) => row.label === option.label && row.kind === 'taxable');
  const add = (option) => {
    const row = rentalIncomeRow(option.label, option.amount, state.profile);
    // One entry per rental: adding it again updates the amount already there.
    const existing = inPlan(option);
    dispatch({ type: 'SET_PROFILE', payload: { otherIncome: existing ? rows.map((r) => (r === existing ? { ...r, annual: row.annual } : r)) : [...rows, row] } });
    setAdded(row);
    track('rental_added_to_plan', { strategy: option.key });
  };

  return (
    <div className="mt-8 border border-surface-border rounded bg-surface-primary p-5">
      <h2 className="text-lg font-semibold text-terminal-cyan mb-2">Count This Rent in Your Retirement Plan</h2>
      <p className="text-sm text-txt-secondary mb-4">
        Adds the rental's yearly cash flow, after the loan and running costs, to your retirement plan as taxed income
        from your retirement age on. It doesn't count the rent before you retire, the down payment or a sale. Add those on the
        Profile page as one-off money if you want them in.
      </p>
      <div className="flex flex-wrap gap-3">
        {options.map((option) => (option.amount > 0 ? (
          <button
            key={option.key}
            type="button"
            disabled={(full && !inPlan(option)) || inPlan(option)?.annual === Math.round(option.amount)}
            onClick={() => add(option)}
            className="glow-btn glow-btn-green min-h-[44px] px-4 py-2 text-sm font-medium rounded disabled:opacity-50"
          >
            {!inPlan(option)
              ? `Add the ${option.name}: ${formatMoney(option.amount)} a year`
              : inPlan(option).annual === Math.round(option.amount)
                ? `The ${option.name} is in your plan: ${formatMoney(option.amount)} a year`
                : `Update the ${option.name} in your plan to ${formatMoney(option.amount)} a year`}
          </button>
        ) : (
          <p key={option.key} className="text-sm text-txt-secondary self-center">
            The {option.name} doesn't make money after its costs, so there's nothing to add.
          </p>
        )))}
      </div>
      {full && <p className="text-sm text-terminal-amber mt-3">Your plan already holds {MAX_LIST_ROWS} other incomes. Remove one on the Profile page first.</p>}
      {added && (
        <p className="text-sm text-terminal-green mt-3" role="status">
          Your plan now counts {formatMoney(added.annual)} a year from this rental, from age {added.fromAge} to {added.toAge}.{' '}
          <Link to="/financial-planning/profile" className="underline text-terminal-cyan">Review it on the Profile page</Link>
        </p>
      )}
    </div>
  );
}
